import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { exportSectionsToPDF } from '../utils/pdfExport';
import {
  A4_RATIO,
  FOOTER_RESERVE,
  PAGE_BUDGET,
  H,
  ledeHeight,
  noteHeight,
  boxHeight,
  rowHeight,
  paginateFlow,
} from './expense_eom.pages';
import './expense_eom.css';

/**
 * EXPENSE — End of Month Report (v2 layout).
 *
 * A cover, then eight numbered sections: the executive summary, the quadrant matrix, the cost
 * drivers, the closing stock carry-forward ledger, the stock movement ledger, the price
 * variance audit, the purchase ledger and the decisions for the month. Long tables run onto
 * "(CONT.)" pages, so the page count follows the month's data (expense_eom.pages.js decides
 * the breaks, then the page is measured and corrected in the browser, as on the Sales EOM).
 *
 * This file is a RENDERER. Every number and every ranking arrives settled from
 * /api/reports/expense-eom, which asserts its own tie-outs before responding. Headings,
 * column labels, quadrant names and the fixed advice are text here; nothing on the page adds
 * up its own column, because the moment it does that column can disagree with the figure
 * above it and the owner has no way to tell which one is wrong. The one exception is the
 * app's recipe cost, merged in by the loader below exactly where it always was.
 *
 * Every rate carries its denominator (₹ / kg, ₹ / L, ₹ / pc). A bare "₹1.04" is ₹1.04 per
 * GRAM — ₹1,040/kg, an ordinary cardamom price that only looked trivial because the unit
 * was missing — and a rate per kg is never ranked against a rate per piece.
 */

// ---------------------------------------------------------------------------
// Display formatting
// ---------------------------------------------------------------------------
/**
 * Indian digit grouping. Paise appear only when the source value actually carries them —
 * a ₹10,41,452 purchase total reads better than ₹10,41,452.00, but a ₹1,461.76 ledger row
 * must not silently lose its 76 paise.
 */
const inr = (n) => {
  const value = Number(n || 0);
  const hasPaise = Math.abs(value - Math.round(value)) > 0.004;
  const text = `₹${Math.abs(value).toLocaleString('en-IN', {
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: hasPaise ? 2 : 0,
  })}`;
  return value < 0 && text !== '₹0' ? `−${text}` : text;
};

/** Whole rupees, for totals rows and the decisions page. */
const inr0 = (n) => {
  const value = Math.round(Number(n || 0));
  const text = `₹${Math.abs(value).toLocaleString('en-IN')}`;
  return value < 0 ? `−${text}` : text;
};

/** A difference: +₹30.82, −₹486.45, ₹0. */
const signedInr = (n, whole = false) => {
  const value = Number(n || 0);
  const text = whole ? inr0(Math.abs(value)) : inr(Math.abs(value));
  if (text === '₹0') return '₹0';
  return value > 0 ? `+${text}` : `−${text}`;
};

/** Up to three decimals; a negative reads with a true minus sign, as everywhere else on the page. */
const num3 = (n) => {
  const value = Number(n || 0);
  const text = Math.abs(value).toLocaleString('en-IN', { maximumFractionDigits: 3 });
  return value < 0 && text !== '0' ? `−${text}` : text;
};

const signedNum = (n) => {
  const value = Number(n || 0);
  if (Math.abs(value) < 0.0005) return '0';
  return value > 0 ? `+${num3(value)}` : `−${num3(Math.abs(value))}`;
};

const qty = (n, unit) =>
  `${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}${unit ? ` ${unit}` : ''}`;

/** A consumed or bought quantity in the unit its rate is quoted in (34.72 kg beside ₹/kg). */
const basisQty = (r) =>
  r.basisQty !== undefined && r.basisQty !== null ? `${num3(r.basisQty)} ${r.basisUnit}` : qty(r.qty, r.unit);

const pct = (n) => (n === null || n === undefined ? '—' : `${Number(n).toFixed(1)}%`);

/** A per-unit cost ALWAYS carries its denominator. */
const rate = (n, basisUnit) => `${inr(n)} / ${basisUnit || 'unit'}`;

const COUNT_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
const plural = (n, one, many = `${one}s`) => `${Number(n || 0).toLocaleString('en-IN')} ${n === 1 ? one : many}`;
const listNames = (names) => {
  const n = (names || []).filter(Boolean);
  if (n.length <= 1) return n[0] || '';
  return `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`;
};

// ---------------------------------------------------------------------------
// Period picker
// ---------------------------------------------------------------------------
/**
 * This report used to render exactly one window — whatever the server resolved from the
 * latest closing count — so a month that had already been closed could never be opened
 * again. Every figure here is period-scoped, which makes "show me last month" the question
 * the owner asks most and the only one the page could not answer.
 *
 * The month list is built on the client from the onboarding date rather than fetched: the
 * server already returns onboardingDate on every response, gated or not. Months with no
 * closing count are deliberately still offered — the server gates those with its own
 * message, which tells the owner WHY the month is unavailable. Hiding them would leave the
 * same owner guessing whether the month is missing or the report is broken.
 */
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad2 = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const parseDay = (d) => {
  if (!d) return null;
  // Parsed at local midnight, never as a bare 'YYYY-MM-DD' (which JS reads as UTC and
  // then prints one day earlier everywhere east of Greenwich).
  const dateObj = new Date(`${String(d).slice(0, 10)}T00:00:00`);
  return isNaN(dateObj.getTime()) ? null : dateObj;
};

const fmtDay = (d) => {
  const dateObj = parseDay(d);
  if (!dateObj) return '';
  return `${pad2(dateObj.getDate())}/${pad2(dateObj.getMonth() + 1)}/${dateObj.getFullYear()}`;
};

/** "Sep 2026 · 01–30 / 09 / 26", or "Aug 2026 – Sep 2026 · 15/08/26 – 30/09/26". */
function periodLabel(start, end) {
  const s = parseDay(start);
  const e = parseDay(end);
  if (!s || !e) return '';
  const yy = (d) => String(d.getFullYear()).slice(2);
  if (s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()) {
    return `${MONTH_NAMES[s.getMonth()]} ${s.getFullYear()} · ${pad2(s.getDate())}–${pad2(e.getDate())} / ${pad2(
      e.getMonth() + 1
    )} / ${yy(e)}`;
  }
  const short = (d) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${yy(d)}`;
  return `${MONTH_NAMES[s.getMonth()]} ${s.getFullYear()} – ${MONTH_NAMES[e.getMonth()]} ${e.getFullYear()} · ${short(
    s
  )} – ${short(e)}`;
}

/** Read the business onboarding date cached at login (Businesses.created_at). */
function readOnboardingDate() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const raw = u.businessCreatedAt || u.business_created_at || u.createdAt || u.created_at;
    return raw ? String(raw).slice(0, 10) : '';
  } catch (_) {
    return '';
  }
}

/**
 * One selectable period per month, from the onboarding month through the current month.
 *   - onboarding month : onboarding date -> that month's last day
 *   - middle months    : 1st -> last day
 *   - current month    : 1st -> today  (the live "to date" period, always last)
 * A leading "all" option keeps the previous behaviour — the whole span, onboarding to the
 * latest closing stock — so the report opens on exactly what it opened on before.
 *
 * Newest month first after "all": the months an owner opens are the last two, and a list
 * that starts at onboarding buries them at the bottom as the year goes on.
 */
function buildPeriods(onboardingDate) {
  const options = [{ key: 'all', label: 'Full period · onboarding → last closing', start: '', end: '', isAll: true }];
  const startD = parseDay(onboardingDate);
  if (!startD) return options;

  const today = new Date();
  const endY = today.getFullYear();
  const endM = today.getMonth();
  const months = [];
  let y = startD.getFullYear();
  let m = startD.getMonth();

  while (y < endY || (y === endY && m <= endM)) {
    const isFirst = y === startD.getFullYear() && m === startD.getMonth();
    const isCurrent = y === endY && m === endM;
    const lastDay = new Date(y, m + 1, 0).getDate();
    months.push({
      key: `${y}-${pad2(m + 1)}`,
      label: isCurrent ? `${MONTH_NAMES[m]} ${y} · to date` : `${MONTH_NAMES[m]} ${y}`,
      start: isFirst ? String(onboardingDate).slice(0, 10) : `${y}-${pad2(m + 1)}-01`,
      end: isCurrent ? ymd(today) : `${y}-${pad2(m + 1)}-${pad2(lastDay)}`,
      isCurrent,
    });
    m += 1;
    if (m > 11) { m = 0; y += 1; }
  }
  return [...options, ...months.reverse()];
}

// ---------------------------------------------------------------------------
// Fixed text
// ---------------------------------------------------------------------------
const SECTION = {
  summary: '01 — EXECUTIVE EXPENSE SUMMARY',
  matrix: '02 — EXPENSE QUADRANT MATRIX',
  drivers: '03 — COST DRIVERS',
  closing: '04 — CLOSING STOCK CARRY-FORWARD',
  movement: '05 — STOCK MOVEMENT',
  prices: '06 — SYSTEM PRICE VARIANCE AUDIT',
  purchases: '07 — PURCHASE LEDGER',
  decisions: '08 — RECOMMENDATIONS',
  // Only when the business has Not in Recipe switched on (the payload then carries it).
  notInRecipe: '09 — NOT IN RECIPE EXPENSES',
};

/**
 * The four quadrants, in the order the matrix draws them: high spend across the top, high
 * cost down the left. The advice is FIXED — the same every month, so the owner learns what a
 * quadrant means once. The split itself is the server's (quadrants[].key).
 */
const QUADRANTS = {
  q1: {
    label: 'Q1 · HIGH SPEND · HIGH COST',
    name: 'THE CRITICAL ANCHORS',
    action: 'These few ingredients are most of your food bill. Get a second quote every month, weigh portions at the pass, and count them every week.',
  },
  q3: {
    label: 'Q3 · HIGH SPEND · LOW COST',
    name: 'CHEAP BUT CONSUMED IN BULK',
    action: 'Cheap per unit but used in bulk, so small leaks add up fast. Negotiate a bulk rate and compare usage with sales.',
  },
  q2: {
    label: 'Q2 · LOW SPEND · HIGH COST',
    name: 'COSTLY BUT USED SPARINGLY',
    action: 'Buy only what the next one to two weeks need, keep them sealed, and check expiry at every count.',
  },
  q4: {
    label: 'Q4 · LOW SPEND · LOW COST',
    name: 'THE PANTRY LONG TAIL',
    action: 'Little money either way. Keep a small minimum stock and reorder on a fixed day.',
  },
};
const MATRIX_ORDER = ['q1', 'q3', 'q2', 'q4'];
const QUADRANT_ROWS_SHOWN = 5;

const MOVEMENT_EXPLAINER = [
  ['Opening + Purchased − Closing = Used (Expected)', 'what left the shelf, by your two counts.'],
  ['Actual Used', 'what your sales and wastage entries recorded.'],
];

// ---------------------------------------------------------------------------
// Page furniture
// ---------------------------------------------------------------------------
function Footer({ page, pages, businessName }) {
  return (
    <div className="eeom-footer">
      Invexis Analytics{businessName ? ` · ${businessName}` : ''} · Expense End-of-Month Report
      <span>|</span>Page {page} of {pages}
    </div>
  );
}

function Page({ ctx, page, section, title, lede, continued, testid, flow, flowPage, children }) {
  return (
    <section
      className="eeom-page"
      data-eeom-section={testid}
      data-eeom-flow={flow}
      data-eeom-flow-page={flow ? flowPage : undefined}
    >
      <div className="eeom-running">
        <span>{ctx.businessName ? `${ctx.businessName} · ` : ''}Expense EOM</span>
        <span>{ctx.periodText}</span>
      </div>
      <p className="eeom-eyebrow">{section}</p>
      <h2>
        {title}
        {continued ? ' (CONT.)' : ''}
      </h2>
      {continued ? (
        <p className="eeom-lede">Continued from the previous page.</p>
      ) : (
        lede && <p className="eeom-lede">{lede}</p>
      )}
      {children}
      <Footer page={page} pages={ctx.pages} businessName={ctx.businessName} />
    </section>
  );
}

function Cover({ ctx }) {
  return (
    <section className="eeom-page eeom-cover" data-eeom-section="cover">
      <h1>EXPENSE</h1>
      <p>End of Month Report</p>
      <span>
        {ctx.businessName ? `${ctx.businessName} · ` : ''}
        {ctx.periodText}
      </span>
      <Footer page={1} pages={ctx.pages} businessName={ctx.businessName} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// 01 — EXECUTIVE EXPENSE SUMMARY
// ---------------------------------------------------------------------------
const barWidth = (value, max) => `${Math.max(max > 0 ? (Math.max(Number(value) || 0, 0) / max) * 100 : 0, 0.6)}%`;

function Summary({ data, ctx, page }) {
  const { summary } = data;
  const { purchases, consumption, closingStock } = summary;
  const appCrossCheck = data.appCrossCheck;
  const period = data.period || {};
  const daily = summary.dailyConsumption || null;
  const movement = data.stockMovement || null;

  // Closing stock is valued from the live batch position, which describes the shelf TODAY.
  // For the latest closed month that is the count the owner just gave; for any earlier
  // month it is not, and the figure must say so rather than pass itself off as that
  // month's count. `=== false` deliberately, not a falsy test: a payload from a server
  // that predates the flag asserts nothing, and must not be labelled as stale.
  const staleClosing = period.isLatestClosedPeriod === false;

  // The Reports screen's Expense card is purchases PLUS wastage under one label, and the
  // owner reads the two screens side by side. So this box shows the SAME total the card
  // does, with the split named underneath — the alternative is two pages that disagree by
  // the wastage figure and a reader with no way to tell which one is short.
  const expenseTotal = appCrossCheck ? appCrossCheck.expenseCard : purchases.total;
  // Banquet wastage is ordinary wastage here — already inside the wastage figure and
  // the total — so it is only named, never added: its cost comes off each banquet's
  // gross profit, and an owner reading the banquet report beside this one should be
  // able to find it.
  const banquetWastageNote =
    appCrossCheck && appCrossCheck.wastageBanquet > 0
      ? `, of which banquet wastage ${inr(appCrossCheck.wastageBanquet)}`
      : '';
  const expenseCaption = appCrossCheck
    ? `${inr(appCrossCheck.purchases)} bought in — ${purchases.billCount} bills, ` +
      `${purchases.lineCount} lines, ${purchases.vendorCount} vendors — plus ` +
      `${inr(appCrossCheck.wastage)} of wastage${banquetWastageNote}. The same figure as the Reports screen.`
    : `Everything bought in during this period — ${purchases.billCount} bills, ${purchases.lineCount} lines, ${purchases.vendorCount} vendors.`;

  const closingCaption = staleClosing
    ? `On the shelf right now, across ${closingStock.itemCount} ingredients — the live position, not this month's count.`
    : `On the shelf at the count${period.closingDate ? ` of ${fmtDay(period.closingDate)}` : ''}, across ${
        closingStock.itemCount
      } ingredients, at the cost of the batches on hand.`;

  // Per day of what physically left the store room (the matrix and ledgers below), so the
  // days of cover describe how fast the shelf actually empties.
  const dailyCaption = daily
    ? `Stock issued from the store room per day — ${inr(data.totalConsumptionValue)} across ${daily.days} days.${
        daily.closingCoverDays !== null && daily.closingCoverDays !== undefined
          ? ` Your closing stock covers about ${daily.closingCoverDays} days at this rate.`
          : ''
      }`
    : 'Not available for this period.';

  const boxes = [
    [appCrossCheck ? 'TOTAL EXPENSE' : 'INVENTORY PURCHASED', inr(expenseTotal), expenseCaption],
    [
      'TOTAL CONSUMPTION',
      inr(consumption.total),
      `Every ingredient the kitchen issued from the store room — cooked, served and binned — across ${consumption.itemCount} ingredients.`,
    ],
    ['CLOSING STOCK HELD', inr(closingStock.total), closingCaption],
    ['AVG DAILY CONSUMPTION', daily ? `${inr(daily.perDay)} / day` : '—', dailyCaption],
  ];

  const bars = [
    ['Purchases', purchases.total],
    ['Consumption', consumption.total],
    ['Closing Stock', closingStock.total],
  ];
  if (movement && movement.available) bars.push(['Stock Shortage', movement.totals.shortValue]);
  const max = Math.max(...bars.map(([, v]) => Number(v) || 0), 0);

  return (
    <Page
      ctx={ctx}
      page={page}
      section={SECTION.summary}
      title="EXECUTIVE EXPENSE SUMMARY"
      lede="What you bought, what the kitchen used, what is on the shelf, and how fast it goes. Inventory only — rent, salaries, utilities and platform fees are not here. The decisions these numbers call for are on the last page."
      testid="summary"
    >
      <div className="eeom-kpis">
        {boxes.map(([label, value, caption]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
            <p>{caption}</p>
          </div>
        ))}
      </div>

      <div className="eeom-chart">
        <h3>Core Expense Metrics (INR)</h3>
        {bars.map(([label, value], index) => (
          <div className="eeom-bar-row" key={label}>
            <span>{label}</span>
            <div>
              <i className={`eeom-pattern eeom-pattern-${index}`} style={{ width: barWidth(value, max) }} />
            </div>
            <strong>{inr(value)}</strong>
          </div>
        ))}
        {movement && movement.available && (
          <p className="eeom-chart-note">
            Stock Shortage: the stock that left the shelf beyond what your sales and wastage entries
            account for — the Short rows of section 05.
          </p>
        )}
      </div>

      {/* An earlier month reported with today's shelf. Stated once, plainly. */}
      {staleClosing && (
        <p className="eeom-note-sm">
          <strong>Closing stock is live, not historical.</strong> Purchases and consumption
          above are windowed to this period, but the closing figure is valued from stock on
          hand today — this system keeps no priced snapshot of the shelf as it stood at an
          earlier month end. Read the closing figure as indicative for this month; the
          purchase and consumption figures are exact.
        </p>
      )}

      {consumption.unpriceableCount > 0 && (
        <p className="eeom-note-sm">
          <strong>Not included above:</strong> {plural(consumption.unpriceableCount, 'ingredient')}{' '}
          {consumption.unpriceableCount === 1 ? 'was' : 'were'} consumed but carry no cost on
          record — no batch price and no purchase price anywhere. They are left out of the
          consumption figures rather than counted at ₹0, so consumption is short by an unknown
          amount rather than by a hidden one. The last page counts them under the gaps in the
          records.
        </p>
      )}
    </Page>
  );
}

// ---------------------------------------------------------------------------
// 02 — EXPENSE QUADRANT MATRIX
// ---------------------------------------------------------------------------
function QuadrantCard({ quadrant, meta, onShowAll }) {
  const rows = (quadrant.rows || []).slice(0, QUADRANT_ROWS_SHOWN);
  const more = Math.max((quadrant.itemCount || 0) - rows.length, 0);
  return (
    <div className="eeom-quad" data-testid={`quadrant-${quadrant.key}`}>
      <h3>{meta.label}</h3>
      <p className="eeom-quad-sub">
        {meta.name} · {plural(quadrant.itemCount || 0, 'ITEM', 'ITEMS')} · {inr(quadrant.totalValue)}
      </p>
      {rows.length === 0 ? (
        <p className="eeom-empty">No ingredients in this quadrant this month</p>
      ) : (
        <table className="eeom-quad-table">
          <tbody>
            {rows.map((r) => (
              <tr key={r.itemId}>
                <td>{r.name}</td>
                <td>{basisQty(r)}</td>
                <td>{inr(r.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {more > 0 && (
        // The same line the PDF prints; on screen it opens the whole quadrant. The hint is
        // hidden during export (handleExport) — "view all" means nothing on paper.
        <button type="button" className="eeom-quad-more" onClick={() => onShowAll(quadrant.key)}>
          +{more} more in this quadrant
          <span className="eeom-quad-more-hint">
            {' · '}
            <span className="eeom-quad-more-link">View all {quadrant.itemCount}</span>
          </span>
        </button>
      )}
      <p className="eeom-quad-action">{meta.action}</p>
    </div>
  );
}

/**
 * Every ingredient in one quadrant, opened from the card's "+N more". The rows are the
 * server's, in the server's order, and the total is the server's — the search only hides
 * rows, it never sums the ones left, so this list cannot disagree with the card behind it.
 * Rendered into <body>, outside the report pages, so a PDF export never captures it.
 */
function QuadrantPopup({ quadrant, meta, onClose }) {
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (searchRef.current) searchRef.current.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const rows = quadrant.rows || [];
  const needle = query.trim().toLowerCase();
  const shown = needle ? rows.filter((r) => String(r.name || '').toLowerCase().includes(needle)) : rows;

  return createPortal(
    <div
      className="eeom-qpop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="eeom-qpop-card" role="dialog" aria-modal="true" aria-labelledby="eeom-qpop-title">
        <div className="eeom-qpop-head">
          <div>
            <h3 id="eeom-qpop-title">{meta.label}</h3>
            <p className="eeom-quad-sub">
              {meta.name} · {plural(quadrant.itemCount || 0, 'ITEM', 'ITEMS')} · {inr(quadrant.totalValue)}
            </p>
          </div>
          <button type="button" className="eeom-qpop-close" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>

        <input
          ref={searchRef}
          type="search"
          className="eeom-qpop-search"
          placeholder="Search ingredients in this quadrant"
          aria-label="Search ingredients in this quadrant"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        <div className="eeom-qpop-body">
          {shown.length === 0 ? (
            <p className="eeom-qpop-empty">No ingredient in this quadrant matches “{query.trim()}”.</p>
          ) : (
            <table className="eeom-qpop-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Ingredient</th>
                  <th>Qty consumed</th>
                  <th>Rate</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.rank}>
                    <td>{r.rank}</td>
                    <td>{r.name}</td>
                    <td>{basisQty(r)}</td>
                    <td>{rate(r.unitCost, r.basisUnit)}</td>
                    <td>{inr(r.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="eeom-qpop-foot">
          <span>
            {needle
              ? `Showing ${shown.length.toLocaleString('en-IN')} of ${plural(rows.length, 'ingredient')}`
              : `All ${plural(rows.length, 'ingredient')}, largest consumption value first`}
          </span>
          <strong>Quadrant total {inr(quadrant.totalValue)}</strong>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Matrix({ data, ctx, page }) {
  const basis = data.quadrantBasis || {};
  const byKey = Object.fromEntries((data.quadrants || []).map((q) => [q.key, q]));
  const lead = data.quadrantLead;
  const [openKey, setOpenKey] = useState(null);
  const closePopup = useCallback(() => setOpenKey(null), []);

  let rule = null;
  if (lead && lead.names && lead.names.length) {
    const names = listNames(lead.names);
    rule =
      lead.quadrant === 'q1'
        ? `Attack Quadrant 1 first: ${names} alone account for ${inr(lead.value)} of consumption — ${pct(
            lead.sharePct
          )} of the month's consumption value. Lock prices with the supplier and weigh portions here — a 5% saving on Quadrant 1 dwarfs any cut in the long tail.`
        : `Nothing is both dear and heavily used this month. Your two largest consumers, ${names}, account for ${inr(
            lead.value
          )} — ${pct(lead.sharePct)} of the month's consumption value. Lock their prices and weigh their portions first.`;
  }

  return (
    <Page
      ctx={ctx}
      page={page}
      section={SECTION.matrix}
      title="CONSUMPTION VALUATION MATRIX"
      lede={
        basis.split === 'equal_quarters'
          ? `Every priced ingredient divided into four equal quarters. High spend = the top half by consumption value${
              basis.highSpendFrom != null ? ` (${inr(basis.highSpendFrom)} and above)` : ''
            }. Each half is then split again on cost: high cost = the dearer half, every rate judged against ingredients measured the same way — kg against kg, L against L, piece against piece.`
          : `Every priced ingredient placed on two questions. High spend = consumption value above the median of ${inr(
              basis.consumptionMedian
            )}. High cost = dearer than the median rate of ingredients measured the same way — kg against kg, L against L, piece against piece.`
      }
      testid="matrix"
    >
      <div className="eeom-matrix">
        {MATRIX_ORDER.map((key) =>
          byKey[key] ? (
            <QuadrantCard key={key} quadrant={byKey[key]} meta={QUADRANTS[key]} onShowAll={setOpenKey} />
          ) : null
        )}
      </div>
      {openKey && byKey[openKey] && (
        <QuadrantPopup quadrant={byKey[openKey]} meta={QUADRANTS[openKey]} onClose={closePopup} />
      )}
      {rule && (
        <div className="eeom-rulebox">
          <h4>OWNER ACTION RULE</h4>
          <p>{rule}</p>
        </div>
      )}
    </Page>
  );
}

// ---------------------------------------------------------------------------
// 03 — COST DRIVERS
// ---------------------------------------------------------------------------
function CostDrivers({ data, ctx, page }) {
  const ingredients = (data.costDrivers && data.costDrivers.topIngredients) || [];
  const vendors = (data.costDrivers && data.costDrivers.topVendors) || [];
  const count = COUNT_WORDS[ingredients.length] || String(ingredients.length);

  return (
    <Page
      ctx={ctx}
      page={page}
      section={SECTION.drivers}
      title="WHERE YOUR FOOD COST GOES"
      lede={`The ${count} ingredient${
        ingredients.length === 1 ? '' : 's'
      } carrying the most consumption value this period, and the vendors you spent the most with.`}
      testid="cost-drivers"
    >
      <h3 className="eeom-subhead">Top Ingredients by Consumption Value</h3>
      {ingredients.length === 0 ? (
        <p className="eeom-empty">No ingredient consumption recorded for this period</p>
      ) : (
        <table className="eeom-table eeom-ranked">
          <thead>
            <tr>
              <th>#</th>
              <th>Ingredient</th>
              <th>Qty Consumed</th>
              <th>Rate</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {ingredients.map((r) => (
              <tr key={r.itemId}>
                <td>{r.rank}</td>
                <td>{r.name}</td>
                <td>{basisQty(r)}</td>
                <td>{rate(r.unitCost, r.basisUnit)}</td>
                <td>{inr(r.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3 className="eeom-subhead">Top Vendors by Spend</h3>
      {vendors.length === 0 ? (
        <p className="eeom-empty">No purchases recorded for this period</p>
      ) : (
        <table className="eeom-table eeom-ranked">
          <thead>
            <tr>
              <th>#</th>
              <th>Vendor</th>
              <th>Spend</th>
            </tr>
          </thead>
          <tbody>
            {vendors.map((r) => (
              <tr key={r.vendorName}>
                <td>{r.rank}</td>
                <td>{r.vendorName}</td>
                <td>{inr(r.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Flow tables (04–07): one table spec per kind of table a flow lays out.
// ---------------------------------------------------------------------------
const TABLES = {
  closing: {
    className: 'eeom-table eeom-closing',
    head: ['Ingredient', 'Closing Balance', 'Unit', 'Unit Cost', 'Carry-Forward Value'],
    height: H.tableHead2,
  },
  movement: {
    className: 'eeom-table eeom-movement',
    head: ['Ingredient (Unit)', 'Opening', '+ Purchased', '− Closing', '= Used (Expected)', 'Actual Used', 'Variance', 'Variance ₹', 'Status'],
    height: H.tableHead2,
  },
  uncounted: {
    className: 'eeom-table eeom-uncounted',
    head: ['Ingredient (Unit)', 'Opening', '+ Purchased', 'Actual Used', 'What to do'],
    height: H.tableHead,
  },
  prices: {
    className: 'eeom-table eeom-prices',
    head: ['Ingredient', 'Baseline Unit Price', 'Current Unit Price', 'Variance %', 'This Month ₹', 'Profit Impact', 'Last Billed By'],
    height: H.tableHead2,
  },
  vendor: {
    className: 'eeom-table eeom-ranked eeom-purchases',
    head: ['#', 'Ingredient', 'Qty', 'Rate', 'Value', 'Share %'],
    height: H.tableHead,
  },
  notInRecipe: {
    className: 'eeom-table eeom-ranked eeom-purchases',
    head: ['Item', 'Quantity', 'Booked On', 'Flag', 'Amount'],
    height: H.tableHead,
  },
};

/** Lay a page's slice of a flow out: consecutive rows of one table become one table. */
function FlowBody({ items }) {
  const out = [];
  let run = null;
  const flush = () => {
    if (!run) return;
    const spec = TABLES[run.spec];
    out.push(
      <table className={spec.className} key={`t-${run.items[0].key}`}>
        <thead>
          <tr>
            {spec.head.map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{run.items.map((it) => React.cloneElement(it.node, { key: it.key }))}</tbody>
      </table>
    );
    run = null;
  };
  for (const it of items) {
    if (it.table) {
      if (!run || run.table !== it.table) {
        flush();
        run = { table: it.table, spec: it.spec, items: [] };
      }
      run.items.push(it);
    } else {
      flush();
      out.push(<React.Fragment key={it.key}>{it.node}</React.Fragment>);
    }
  }
  flush();
  return out;
}

// ---- 04 closing stock ------------------------------------------------------
function closingFlow(data) {
  const ledger = data.closingLedger;
  if (!ledger) {
    return [{ key: 'closing-none', height: 60, node: <p className="eeom-empty">The closing ledger is not available for this period.</p> }];
  }
  const rows = ledger.rows || [];
  if (rows.length === 0) {
    return [{ key: 'closing-empty', height: 60, node: <p className="eeom-empty">Nothing was on the shelf at the count.</p> }];
  }
  const items = rows.map((r, i) => ({
    key: `closing-${r.itemId}`,
    table: 'closing',
    spec: 'closing',
    height: rowHeight(r.name, 28),
    keepWithNext: i === rows.length - 1,
    node: (
      <tr>
        <td>{r.name}</td>
        <td>{num3(r.qty)}</td>
        <td>{r.unit}</td>
        <td>
          {rate(r.unitCost, r.basisUnit)}
          {r.priceFromBill ? ' *' : ''}
        </td>
        <td>{inr(r.value)}</td>
      </tr>
    ),
  }));
  items.push({
    key: 'closing-total',
    table: 'closing',
    spec: 'closing',
    height: H.totalRow,
    node: (
      <tr className="eeom-total-row">
        <td colSpan={4}>TOTAL CARRY-FORWARD VALUE</td>
        <td>{inr(ledger.total)}</td>
      </tr>
    ),
  });
  if (ledger.priceFromBillCount > 0) {
    const text = 'No batch cost on record: the latest purchase price is used for that row.';
    items.push({
      key: 'closing-note',
      height: noteHeight(text),
      node: (
        <p className="eeom-footnote">
          <strong>* {text.split(':')[0]}:</strong>
          {text.slice(text.indexOf(':') + 1)}
        </p>
      ),
    });
  }
  return items;
}

// ---- 05 stock movement -----------------------------------------------------
function movementFlow(data) {
  const sm = data.stockMovement;
  if (!sm || !sm.available) {
    const text =
      sm && sm.reason === 'error'
        ? 'Stock movement could not be measured for this period. Every other figure in this report is unaffected.'
        : 'No closing count brackets this period, so stock movement cannot be measured yet. It appears once a month is closed by a count.';
    return [{ key: 'movement-none', height: 70, node: <p className="eeom-empty">{text}</p> }];
  }

  const items = [];
  const explainer = (
    <div className="eeom-howto">
      <h4>HOW TO READ THIS TABLE</h4>
      {MOVEMENT_EXPLAINER.map(([term, means]) => (
        <p key={term}>
          <strong>{term}</strong> — {means}
        </p>
      ))}
      <p>
        <strong>Variance</strong> = Actual Used − Used (Expected). <strong>Short</strong> (minus): more
        stock left the shelf than was recorded — check portions, wastage entries and theft.{' '}
        <strong>Excess</strong> (plus): more was recorded than left — usually a bill not entered.
        Within ±{sm.tolerancePct}% of the stock you had, or under ₹{sm.valueFloor}, is <strong>OK</strong>.
      </p>
    </div>
  );
  items.push({ key: 'movement-howto', height: 196, node: explainer });

  const rows = sm.rows || [];
  rows.forEach((r, i) => {
    const derived = !r.openingCounted;
    items.push({
      key: `movement-${r.itemId}`,
      table: 'movement',
      spec: 'movement',
      height: rowHeight(`${r.name} (${r.unit})`, 17),
      keepWithNext: i === rows.length - 1,
      node: (
        <tr className={r.status === 'OK' ? '' : 'eeom-flagged'}>
          <td>
            <b>{r.name}</b> <em>({r.unit})</em>
          </td>
          <td>
            {derived ? '~' : ''}
            {num3(r.opening)}
          </td>
          <td>{num3(r.purchased)}</td>
          <td>{num3(r.closing)}</td>
          <td className="eeom-strong">{num3(r.usedExpected)}</td>
          <td>{num3(r.actualUsed)}</td>
          <td>{r.status === 'Check unit' ? '—' : signedNum(r.variance)}</td>
          <td>{r.status === 'Check unit' || r.varianceValue === null ? '—' : signedInr(r.varianceValue)}</td>
          <td className={`eeom-status eeom-status-${String(r.status).toLowerCase().replace(/\s+/g, '-')}`}>{r.status}</td>
        </tr>
      ),
    });
  });

  const t = sm.totals;
  if (rows.length > 0) {
    items.push({
      key: 'movement-total',
      table: 'movement',
      spec: 'movement',
      height: H.totalRow,
      node: (
        <tr className="eeom-total-row">
          <td>TOTAL ₹</td>
          <td>{inr0(t.opening)}</td>
          <td>{inr0(t.purchased)}</td>
          <td>{inr0(t.closing)}</td>
          <td>{inr0(t.usedExpected)}</td>
          <td>{inr0(t.actualUsed)}</td>
          <td />
          <td>{signedInr(t.variance, true)}</td>
          <td />
        </tr>
      ),
    });
    const parts = [
      `${plural(t.shortCount, 'ingredient')} ${t.shortCount === 1 ? 'is' : 'are'} short`,
      `${t.excessCount} in excess`,
      `${t.okCount} OK`,
    ];
    if (t.checkUnitCount > 0) parts.push(`${t.checkUnitCount} counted in a unit that does not match the shelf`);
    const summaryText = `In total: ${listNames(parts)}. Quantities are per the unit beside each name; every rupee figure uses that ingredient's rate from this report — the cost of the batches on hand.`;
    items.push({
      key: 'movement-summary',
      height: noteHeight(summaryText),
      node: (
        <p className="eeom-note-sm">
          <strong>In total:</strong> {summaryText.slice('In total: '.length)}
        </p>
      ),
    });
    if (sm.openingDerivedCount > 0) {
      const text = `~ Opening worked out from the stock ledger: ${plural(
        sm.openingDerivedCount,
        'ingredient'
      )} had no earlier count to start from. Counting ${
        sm.openingDerivedCount === 1 ? 'it' : 'them'
      } at every close makes next month's figure exact.`;
      items.push({ key: 'movement-derived', height: noteHeight(text), node: <p className="eeom-footnote">{text}</p> });
    }
  } else {
    items.push({
      key: 'movement-no-rows',
      height: 60,
      node: <p className="eeom-empty">No counted ingredient moved this period.</p>,
    });
  }

  const uncounted = sm.uncounted || [];
  if (uncounted.length > 0) {
    items.push({
      key: 'uncounted-head',
      height: H.subhead,
      keepWithNext: true,
      node: <h3 className="eeom-subhead">Moved but not counted — {plural(uncounted.length, 'ingredient')}</h3>,
    });
    uncounted.forEach((r, i) => {
      items.push({
        key: `uncounted-${r.itemId}`,
        table: 'uncounted',
        spec: 'uncounted',
        height: rowHeight(`${r.name} (${r.unit})`, 30),
        keepWithNext: i === uncounted.length - 1,
        node: (
          <tr>
            <td>
              <b>{r.name}</b> <em>({r.unit})</em>
            </td>
            <td>
              {r.openingCounted ? '' : '~'}
              {num3(r.opening)}
            </td>
            <td>{num3(r.purchased)}</td>
            <td>{num3(r.actualUsed)}</td>
            <td>Add to the next count</td>
          </tr>
        ),
      });
    });
    const why =
      'Why these are separate: with no closing count there is nothing to compare usage against, so no variance can be worked out for them.';
    items.push({
      key: 'uncounted-why',
      height: noteHeight(why),
      node: (
        <p className="eeom-footnote">
          <strong>Why these are separate:</strong>
          {why.slice('Why these are separate:'.length)}
        </p>
      ),
    });
  }
  return items;
}

function movementLede(data) {
  const sm = data.stockMovement;
  if (!sm || !sm.available) {
    return 'What you had at the last count, plus what you bought, minus what is left at this count, is what was used. That is compared with what your sales and wastage entries say was used.';
  }
  const from = sm.openingCountDate
    ? `what you had at the count of ${fmtDay(sm.openingCountDate)}`
    : 'what you had when the month began (worked out from the stock ledger — there is no earlier count)';
  const month = parseDay(`${sm.month}-01`);
  const scope =
    sm.sameWindowAsReport === false && month
      ? ` This section covers ${MONTH_NAMES[month.getMonth()]} ${month.getFullYear()}, the month your latest count closes.`
      : '';
  return `For every ingredient you counted: ${from}, plus what you bought, minus what was left at the count of ${fmtDay(
    sm.closingCountDate
  )}, is what was used. That is compared with what your sales and wastage entries say was used — the difference is stock nobody can account for.${scope}`;
}

// ---- 06 price audit --------------------------------------------------------
function pricesFlow(data) {
  const audit = data.priceAudit;
  if (!audit) {
    return [{ key: 'prices-none', height: 60, node: <p className="eeom-empty">The price audit is not available for this period.</p> }];
  }
  const rows = audit.rows || [];
  const items = rows.map((r, i) => ({
    key: `price-${r.itemId}`,
    table: 'prices',
    spec: 'prices',
    height: rowHeight(r.name, 18) + (r.favourable && r.impact !== 'High' ? H.rowLine : 0),
    keepWithNext: i === rows.length - 1,
    node: (
      <tr>
        <td>{r.name}</td>
        <td>{rate(r.baselinePrice, r.basisUnit)}</td>
        <td>{rate(r.currentPrice, r.basisUnit)}</td>
        <td>
          {r.variancePct > 0 ? '+' : r.variancePct < 0 ? '−' : ''}
          {Math.abs(r.variancePct).toFixed(1)}%
        </td>
        <td>{signedInr(r.monthImpact)}</td>
        <td className={r.favourable ? 'eeom-favourable' : `eeom-impact-${r.impact.toLowerCase()}`}>
          {r.impact}
          {r.favourable ? ' (favourable)' : ''}
        </td>
        <td>{r.lastBilledBy || '—'}</td>
      </tr>
    ),
  }));
  if (rows.length === 0) {
    items.push({
      key: 'prices-empty',
      height: 70,
      node: (
        <p className="eeom-empty">
          No ingredient used this period moved its price by {audit.noisePct}% or more against its usual price.
        </p>
      ),
    });
  }

  const cover = `${plural(audit.unauditableCount, 'ingredient')} ${
    audit.unauditableCount === 1 ? 'has' : 'have'
  } fewer than two bills on record, so no movement can be measured for ${
    audit.unauditableCount === 1 ? 'it' : 'them'
  } yet. Moves under ${audit.noisePct}% are ordinary bill-to-bill noise and are left out. A short table means few prices moved — not that the rest were checked and held.`;
  items.push({
    key: 'prices-cover',
    height: noteHeight(`What this table does not cover: ${cover}`),
    node: (
      <p className="eeom-note-sm">
        <strong>What this table does not cover:</strong> {cover}
      </p>
    ),
  });

  const r = audit.rises;
  const f = audit.falls;
  const moves = [];
  if (r.count > 0) moves.push(`${plural(r.count, 'price')} rose, costing ${inr(r.total)} extra this month`);
  if (f.count > 0) moves.push(`${f.count} fell, saving ${inr(f.total)}`);
  const howto = `${moves.length ? `${moves.join('; ')}. ` : ''}Start with the High rows: show the vendor your usual price, ask for a second quote, and check the dishes that use the ingredient still make their margin. High = the change is ${audit.highPct}% or more of the month's consumption value on its own; Medium = ${audit.mediumPct}–${audit.highPct}%.`;
  items.push({
    key: 'prices-howto',
    height: boxHeight(howto),
    node: (
      <div className="eeom-howto">
        <h4>HOW TO USE THIS TABLE</h4>
        <p>{howto}</p>
      </div>
    ),
  });
  return items;
}

// ---- 07 purchase ledger ----------------------------------------------------
function purchasesFlow(data) {
  const rows = data.purchaseLedger || [];
  const total = Number(data.totalPurchaseValue) || 0;
  const p = (data.summary && data.summary.purchases) || {};
  if (rows.length === 0) {
    return [{ key: 'purchases-empty', height: 60, node: <p className="eeom-empty">No purchases recorded for this period</p> }];
  }

  // Grouped by vendor for display; every value and share is the server's.
  const vendorMap = new Map();
  rows.forEach((r) => {
    const name = r.vendorName || 'Other / Direct';
    if (!vendorMap.has(name)) vendorMap.set(name, { vendorName: name, items: [], totalValue: 0 });
    const group = vendorMap.get(name);
    group.items.push(r);
    group.totalValue += Number(r.value || 0);
  });
  const vendors = Array.from(vendorMap.values()).sort((a, b) => b.totalValue - a.totalValue);
  // Inside each vendor, the biggest money first — the server's ledger order.
  vendors.forEach((v) => v.items.sort((a, b) => Number(b.value || 0) - Number(a.value || 0)));

  const items = [];
  vendors.forEach((v, vi) => {
    const table = `vendor-${vi}`;
    const share = total ? (v.totalValue / total) * 100 : 0;
    items.push({
      key: `${table}-head`,
      height: H.subhead,
      keepWithNext: true,
      node: (
        <h3 className="eeom-vendor-head">
          Vendor: {v.vendorName} · {inr(v.totalValue)} · {pct(share)} of spend
        </h3>
      ),
    });
    v.items.forEach((r, n) => {
      items.push({
        key: `${table}-${r.itemId}-${n}`,
        table,
        spec: 'vendor',
        height: rowHeight(r.name, 40),
        keepWithNext: n === v.items.length - 1,
        node: (
          <tr>
            <td>{n + 1}</td>
            <td>{r.name}</td>
            <td>{basisQty(r)}</td>
            <td>{rate(r.rate, r.basisUnit)}</td>
            <td>{inr(r.value)}</td>
            <td>{pct(r.sharePct)}</td>
          </tr>
        ),
      });
    });
    items.push({
      key: `${table}-subtotal`,
      table,
      spec: 'vendor',
      height: H.totalRow,
      node: (
        <tr className="eeom-total-row">
          <td colSpan={4}>SUBTOTAL — {v.vendorName}</td>
          <td>{inr(v.totalValue)}</td>
          <td>{pct(share)}</td>
        </tr>
      ),
    });
  });
  items.push({
    key: 'purchases-grand',
    height: H.grandTotal,
    node: (
      <div className="eeom-grand">
        <h4>TOTAL PURCHASED ACROSS ALL VENDORS</h4>
        <p>
          <strong>{inr(total)}</strong>
          <em>
            {' '}
            · {plural(p.billCount || 0, 'bill')} · {plural(p.lineCount || 0, 'line')} · {plural(p.vendorCount || 0, 'vendor')}
          </em>
        </p>
      </div>
    ),
  });
  return items;
}

// ---- 09 Not in Recipe ------------------------------------------------------
const NIR_FLAG = {
  COUNT_ABOVE_AVAILABLE: 'Counted above available',
  NOT_COUNTED: 'Not counted',
  NO_PRICE: 'No price',
};

/**
 * What the month-end count booked for the items no recipe uses, by expense head. Every figure
 * is the server's (routes/expenseEomReport.js → notInRecipe), which balances the section itself;
 * nothing here adds a column of its own.
 */
function notInRecipeFlow(data) {
  const s = data.notInRecipe;
  if (!s || !s.available) {
    return [{ key: 'nir-none', height: 70, node: <p className="eeom-empty">The Not in Recipe section could not be built for this period. Every other figure in this report is unaffected.</p> }];
  }
  if (!s.booked) {
    return [{ key: 'nir-not-booked', height: 60, node: <p className="eeom-empty">{s.note}</p> }];
  }
  const items = [];
  s.groups.forEach((g, gi) => {
    const table = `nir-${gi}`;
    const countsAs = g.counts_as === 'food_cost' ? 'food cost' : g.counts_as === 'operating_expense' ? 'operating expense' : null;
    items.push({
      key: `${table}-head`,
      height: H.subhead,
      keepWithNext: true,
      node: <h3 className="eeom-vendor-head">{g.label} · {inr(g.subtotal)}{countsAs ? ` · ${countsAs}` : ''}</h3>,
    });
    g.rows.forEach((r, n) => {
      items.push({
        key: `${table}-${r.itemId}`,
        table,
        spec: 'notInRecipe',
        height: rowHeight(r.name, 40),
        keepWithNext: n === g.rows.length - 1,
        node: (
          <tr>
            <td>{r.name}</td>
            <td>{r.qty === null || r.qty === undefined ? '—' : `${num3(r.qty)} ${r.unit}`}</td>
            <td>{r.basis === 'used' ? 'Used' : 'Bought'}</td>
            <td>{(r.flags || []).map((f) => NIR_FLAG[f]).filter(Boolean).join(', ') || '—'}</td>
            <td>{inr(r.amount)}</td>
          </tr>
        ),
      });
    });
    items.push({
      key: `${table}-subtotal`,
      table,
      spec: 'notInRecipe',
      height: H.totalRow,
      node: (
        <tr className="eeom-total-row">
          <td colSpan={4}>SUBTOTAL — {g.label}</td>
          <td>{inr(g.subtotal)}</td>
        </tr>
      ),
    });
  });
  items.push({
    key: 'nir-grand',
    height: H.grandTotal,
    node: (
      <div className="eeom-grand">
        <h4>BOOKED AS NOT IN RECIPE EXPENSE · {s.month_label}</h4>
        <p>
          <strong>{inr(s.total)}</strong>
          <em>
            {' '}
            · food cost {inr(s.food_cost)} · operating expense {inr(s.operating_expense)}
            {s.not_classified ? ` · not classified yet ${inr(s.not_classified)}` : ''}
          </em>
        </p>
      </div>
    ),
  });
  const including = `Inventory expense including Not in Recipe: ${inr(s.inventory_expense_including)} — this report's consumption ${inr(s.consumption_total)} plus Not in Recipe ${inr(s.total)}. It sits beside the totals above; none of them includes it.`;
  items.push({
    key: 'nir-including',
    height: noteHeight(including),
    node: <p className="eeom-note-sm"><strong>{including.split(':')[0]}:</strong>{including.slice(including.indexOf(':') + 1)}</p>,
  });
  const t = s.kitchen_use_trend;
  if (t) {
    const text = `Kitchen use is ${pct(t.this_month_pct)} of sales this month, against ${pct(t.average_pct)} over the previous ${t.previous_months} booked months${
      t.flagged ? ` — ${signedInr(t.difference, true)} above that pace. A dish may be missing an ingredient in its recipe.` : '.'}`;
    items.push({
      key: 'nir-trend',
      height: noteHeight(text),
      node: <p className={`eeom-note-sm${t.flagged ? ' eeom-warn' : ''}`}><strong>Kitchen use trend:</strong> {text}</p>,
    });
  }
  return items;
}

const NIR_LEDE =
  'Items no recipe uses — frying oil, staff tea, packaging, cleaning supplies — booked at the month-end count under the head given to each one. Kitchen use is food cost; every other head is an operating expense. This section is separate: no total elsewhere in this report includes it.';

// ---------------------------------------------------------------------------
// 08 — RECOMMENDATIONS
// ---------------------------------------------------------------------------
function Decisions({ data, ctx, page }) {
  const recs = data.recommendations || [];
  return (
    <Page
      ctx={ctx}
      page={page}
      section={SECTION.decisions}
      title="WHAT TO DECIDE THIS MONTH"
      lede="The decisions this month's numbers call for, in order of the money involved — each with the evidence, the action, who owns it and by when."
      testid="recommendations"
    >
      {recs.length === 0 ? (
        <p className="eeom-empty">Nothing in this month's numbers calls for a decision.</p>
      ) : (
        <table className="eeom-table eeom-decisions">
          <thead>
            <tr>
              <th>#</th>
              <th>What Is Happening</th>
              <th>Decision</th>
              <th>₹ / Month</th>
              <th>Owner · By</th>
            </tr>
          </thead>
          <tbody>
            {recs.map((r, i) => (
              <tr key={r.key}>
                <td className="eeom-dec-num">{pad2(i + 1)}</td>
                <td>
                  <span className="eeom-dec-area">{r.area}</span>
                  <strong>{r.headline}</strong>
                  <em>{r.evidence}</em>
                </td>
                <td>{r.decision}</td>
                <td className="eeom-dec-amount">
                  {r.amount === null || r.amount === undefined ? '—' : `${r.approx ? '≈ ' : ''}${inr0(r.amount)}`}
                </td>
                <td>
                  <strong>{r.owner}</strong>
                  <em>{r.by}</em>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="eeom-note-sm">
        <strong>Reading this page:</strong> ₹ / Month is the money involved each month — the size of
        the problem, or the saving once fixed (≈ marks an estimate). Rows overlap in places, so they
        are not added together. Work from the top: the first two or three carry most of the money.
      </p>

      <div className="eeom-howto eeom-review">
        <h4>YOUR MONTHLY REVIEW — 15 MINUTES</h4>
        <ol>
          <li>
            <strong>Choose</strong> up to three rows above and write one owner and one date against each.
          </li>
          <li>
            <strong>Check</strong> Quadrant 1 (section 02) and the Short rows of Stock Movement (section 05).
          </li>
          <li>
            <strong>Call</strong> the vendor on every High row of the Price Audit (section 06) before the next order.
          </li>
          <li>
            <strong>Next month</strong>, open this page first: did each number go down?
          </li>
        </ol>
      </div>
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Page list
// ---------------------------------------------------------------------------
const NO_SHAVES = {};
// Measuring stops after this many corrections, whatever the pages look like.
const MAX_MEASURE_PASSES = 60;
// Below this page width the layout is a phone view, not an A4 sheet.
const MIN_MEASURE_WIDTH = 700;

/** Flow pages get `${flow}` for their first page and `${flow}-2`, `-3`… after it. */
const flowPageId = (flow, index) => (index === 0 ? flow : `${flow}-${index + 1}`);

const closingLede = (data) => {
  const period = data.period || {};
  if (period.isLatestClosedPeriod === false) {
    return 'What is on the shelf right now — the live position, not this month\'s count — valued at the cost of the batches you still hold. No priced snapshot of the shelf at an earlier month end exists.';
  }
  return `The stock in storage at the count${
    period.closingDate ? ` of ${fmtDay(period.closingDate)}` : ''
  }, valued at the cost of the batches you still hold. These balances carry forward as next month's opening stock.`;
};

const PRICES_LEDE =
  'This audit flags hidden supplier price changes by comparing what you pay for an ingredient now against its own usual price — the average of its previous five bills. The rupee column is what the change actually cost you on what the kitchen used this month.';
const PURCHASES_LEDE =
  'Every purchase line this period, grouped by vendor and ranked by value. Corrective “negative stock fix” entries are excluded — they are data repairs shaped like bills, not money that left the business.';

/**
 * Every page this month's data produces, in reading order. `shaves` holds measured overflow,
 * in pixels, per flow page (`${flow}:${index}`); it only ever shrinks a page's budget, so
 * items move forward and never back.
 */
function buildPageList(data, shaves) {
  if (!data) return [];
  const budgetFor = (flow) => (index) => PAGE_BUDGET - (shaves[`${flow}:${index}`] || 0);
  const pages = [
    { id: 'cover', kind: 'cover' },
    { id: 'summary', kind: 'summary' },
    { id: 'matrix', kind: 'matrix' },
    { id: 'cost-drivers', kind: 'drivers' },
  ];

  const flow = (name, section, title, lede, items) => {
    const tableHead = (table) => {
      const spec = items.find((it) => it.table === table);
      return spec ? TABLES[spec.spec].height : H.tableHead;
    };
    paginateFlow(items, {
      firstHeader: H.header + ledeHeight(lede),
      contHeader: H.header + H.contLede,
      budget: budgetFor(name),
      tableHead,
    }).forEach((slice, index) => {
      pages.push({
        id: flowPageId(name, index),
        kind: 'flow',
        flow: name,
        flowPage: index,
        section,
        title,
        lede,
        continued: index > 0,
        items: items.slice(slice.from, slice.to),
      });
    });
  };

  flow('closing', SECTION.closing, 'CLOSING STOCK CARRY-FORWARD LEDGER', closingLede(data), closingFlow(data));
  flow('movement', SECTION.movement, 'STOCK MOVEMENT LEDGER', movementLede(data), movementFlow(data));
  flow('prices', SECTION.prices, 'SYSTEM PRICE VARIANCE AUDIT', PRICES_LEDE, pricesFlow(data));
  flow('purchases', SECTION.purchases, 'WHAT YOU BOUGHT, AND FROM WHOM', PURCHASES_LEDE, purchasesFlow(data));
  pages.push({ id: 'recommendations', kind: 'decisions' });
  if (data.notInRecipe) {
    flow('not-in-recipe', SECTION.notInRecipe, 'WHAT NO RECIPE USES, BOOKED AS EXPENSE', NIR_LEDE, notInRecipeFlow(data));
  }
  return pages;
}

// ---------------------------------------------------------------------------
export default function ExpenseEom({ goTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gated, setGated] = useState(false);
  // Lets the owner close the gate card and stay on the page to pick another month. Without
  // it the gate is a dead end: the one control that could fix it sits behind the overlay.
  const [gateDismissed, setGateDismissed] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Onboarding date drives the month list. Seeded from the login cache, then corrected
  // once from the first server response if the cache was empty or stale.
  const [onboardingDate, setOnboardingDate] = useState(readOnboardingDate);
  const [selectedKey, setSelectedKey] = useState('all');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const rootRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const periods = useMemo(() => buildPeriods(onboardingDate), [onboardingDate]);
  const selected = useMemo(
    () => periods.find((p) => p.key === selectedKey) || periods[0],
    [periods, selectedKey]
  );

  useEffect(() => {
    const businessId = localStorage.getItem('businessId');
    if (!businessId) {
      setError('No business ID found');
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setGated(false);
        setGateDismissed(false);
        // No dates: the server defaults to onboarding -> latest confirmed closing stock,
        // and gates the report until closing stock has been given at least once. A chosen
        // month sends its own window, which the server gates on the count that closes THAT
        // month and ends at that count.
        const qs = new URLSearchParams({ businessId });
        if (selected && !selected.isAll && selected.start && selected.end) {
          qs.set('startDate', selected.start);
          qs.set('endDate', selected.end);
        }
        const res = await fetch(`/api/reports/expense-eom?${qs.toString()}`);
        const json = await res.json();
        if (cancelled) return;

        // Every response carries the onboarding date, gated or not. Taking it from the
        // server means the month list is right even when the login cache never held one —
        // which is the difference between a full picker and a picker with only "all".
        const respOnboarding = json.period?.onboardingDate || json.data?.period?.onboardingDate;
        if (respOnboarding && !onboardingDate) {
          setOnboardingDate(String(respOnboarding).slice(0, 10));
        }

        if (json.gated) {
          setGated(true);
          setData(null);
        } else if (json.success) {
          const reportData = json.data;
          try {
            const token = localStorage.getItem('token');
            const startD = reportData.period?.startDate;
            const endD = reportData.period?.endDate;
            // Asked for THIS report's exact window, not for the calendar month the window
            // happens to start in. The overlay used to send period=month&monthYear, which
            // is only the same question when the window is a whole calendar month: on the
            // full-period report it answered for May and wrote May's cost onto a
            // May-to-August page, and on a month closed by a mid-month count it answered
            // for all 31 days of a window that ends on the 18th. weekStart/weekEnd is the
            // same endpoint answering for the days actually being reported.
            const normQs = new URLSearchParams({ businessId });
            if (startD && endD) {
              normQs.set('weekStart', String(startD).slice(0, 10));
              normQs.set('weekEnd', String(endD).slice(0, 10));
            }
            const normRes = startD && endD
              ? await fetch(`/api/reports/summary?${normQs.toString()}`, {
                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                })
              : null;
            if (normRes && normRes.ok) {
              const normJson = await normRes.json();
              const mfgCost = normJson.display_total_cogs !== undefined && normJson.display_total_cogs !== null
                ? Number(normJson.display_total_cogs)
                : Number(normJson.total_cogs || 0);

              // The Reports screen's Expense card is purchases PLUS wastage, under one
              // label. The server computes it from the very function behind
              // /reports/stockout-summary, against this report's own window, and publishes
              // purchases + wastage as `summary.totalExpense` — so the box below shows the
              // Expense card's figure by construction.
              //
              // It used to be a THIRD request fired from here, wrapped in a catch that only
              // logged. When it failed the box silently fell back to purchases alone and
              // the page quietly disagreed with the card by the whole wastage figure, with
              // nothing on screen to say so.
              const serverTotal = reportData.summary && reportData.summary.totalExpense;
              if (serverTotal) {
                reportData.appCrossCheck = {
                  purchases: Number(serverTotal.purchases || 0),
                  wastage: Number(serverTotal.wastage || 0),
                  // The banquet part of `wastage` — display only, already inside it.
                  wastageBanquet: Number(serverTotal.wastageBanquet || 0),
                  expenseCard: Number(serverTotal.total || 0),
                };
              }

              if (mfgCost > 0 && reportData.summary) {
                if (reportData.summary.consumption) {
                  reportData.summary.consumption.total = mfgCost;
                }
                if (reportData.summary.reconciliation) {
                  reportData.summary.reconciliation.consumption = mfgCost;
                  const pTotal = Number(reportData.summary.reconciliation.purchases || 0);
                  const cTotal = Number(reportData.summary.reconciliation.closing || 0);
                  const gap = Number((pTotal - mfgCost - cTotal).toFixed(2));
                  reportData.summary.reconciliation.gap = gap;
                  reportData.summary.reconciliation.gapPctOfPurchases = pTotal > 0 ? Number(((gap / pTotal) * 100).toFixed(2)) : null;
                }
              }
            }
          } catch (e) {
            console.warn('Could not overlay normal report manufacturing cost into expense EOM:', e);
          }
          // The app-summary call above is a SECOND wait. A period picked while it was in
          // flight has already loaded its own report; without this check the slower, stale
          // answer (the full period takes far longer) landed last and overwrote it — "Sep
          // 2026" in the picker over a 05/02–30/09 report.
          if (cancelled) return;
          setData(reportData);
        } else {
          // A tie-out failure is reported plainly rather than dressed up as a network
          // problem. The server refused to ship numbers it could not balance, and the
          // owner is better served knowing that than seeing a plausible wrong report.
          setError(json.message || 'Failed to load report');
        }
      } catch (err) {
        if (!cancelled) setError('Failed to fetch data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, onboardingDate]);

  // Label the report by the resolved reporting window rather than "today".
  const p = (data && data.period) || {};
  const cycleLabel =
    p.startDate && p.endDate
      ? `${fmtDay(p.startDate)} – ${fmtDay(p.endDate)}`
      : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const businessName = (data && data.meta && data.meta.businessName) || '';

  // ---- Page breaking ------------------------------------------------------
  // Estimated breaks first (expense_eom.pages.js), then measured: any page whose content
  // runs into the footer zone has its budget shrunk by the overflow and the list is rebuilt,
  // so items move forward until every page fits one A4 sheet. Corrections belong to the
  // data they were measured against and are dropped when the data or the width changes.
  const [shaveState, setShaveState] = useState({ data: null, map: {} });
  const shaves = shaveState.data === data ? shaveState.map : NO_SHAVES;
  const passes = useRef({ data: null, count: 0 });
  const [measureTick, setMeasureTick] = useState(0);

  const pageList = useMemo(() => buildPageList(data, shaves), [data, shaves]);

  useEffect(() => {
    // Web fonts change line heights; measure again once they are in.
    let alive = true;
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => alive && setMeasureTick((n) => n + 1));
    }
    let timer = null;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        // A new width means new line wraps: start the corrections over.
        setShaveState({ data: null, map: {} });
        passes.current = { data: null, count: 0 };
        setMeasureTick((n) => n + 1);
      }, 200);
    };
    window.addEventListener('resize', onResize);
    return () => {
      alive = false;
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !data) return;
    if (passes.current.data !== data) passes.current = { data, count: 0 };
    if (passes.current.count >= MAX_MEASURE_PASSES) return;

    for (const el of root.querySelectorAll('[data-eeom-flow]')) {
      const width = el.offsetWidth;
      // No layout (jsdom) or a phone-width page: the estimates stand.
      if (!width || width < MIN_MEASURE_WIDTH) return;
      const limit = width * A4_RATIO - FOOTER_RESERVE;
      const top = el.getBoundingClientRect().top;
      let bottom = 0;
      for (const child of el.children) {
        if (child.classList.contains('eeom-footer')) continue;
        bottom = Math.max(bottom, child.getBoundingClientRect().bottom - top);
      }
      if (bottom > limit) {
        const key = `${el.dataset.eeomFlow}:${el.dataset.eeomFlowPage}`;
        const overflow = Math.ceil(bottom - limit) + 4;
        passes.current.count += 1;
        setShaveState((s) => {
          const map = s.data === data ? s.map : {};
          return { data, map: { ...map, [key]: (map[key] || 0) + overflow } };
        });
        return;
      }
    }
  }, [data, pageList, measureTick]);

  /**
   * Every page that rendered, in reading order — the page count follows the month's data.
   * Each page is exactly one A4 sheet, so each becomes exactly one PDF page.
   */
  const handleExport = async () => {
    if (!rootRef.current) return;
    setExporting(true);
    try {
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const sectionSelectors = [...rootRef.current.querySelectorAll('[data-eeom-section]')].map(
        (el) => `[data-eeom-section="${el.dataset.eeomSection}"]`
      );
      await exportSectionsToPDF({
        container: rootRef.current,
        sectionSelectors,
        title: 'Expense EOM Report',
        periodLabel: cycleLabel,
        forcePageBreakPerSection: true,
        fullPageMode: true,
        hideSelectorsDuringCapture: ['.eeom-toolbar', '.eeom-quad-more-hint'],
      });
    } catch (err) {
      setError(`PDF export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  /**
   * The picker sits above every state — report, gate, loading and error alike. A gated
   * month whose only control is hidden behind the gate is how the report came to look
   * broken rather than unavailable.
   */
  const toolbar = (
    <div className="eeom-toolbar">
      <div className="eeom-toolbar-left">
        <span className="eeom-period-label">Period</span>
        <div className="eeom-dropdown-container" ref={dropdownRef}>
          <button
            type="button"
            className="eeom-dropdown-trigger"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            aria-haspopup="listbox"
            aria-expanded={dropdownOpen}
          >
            {selected?.label || 'Select period'}
            <span className="eeom-dropdown-arrow">▾</span>
          </button>
          {dropdownOpen && (
            <div className="eeom-dropdown-menu" role="listbox">
              {periods.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  role="option"
                  aria-selected={selectedKey === option.key}
                  className={`eeom-dropdown-item${
                    selectedKey === option.key ? ' eeom-dropdown-item-active' : ''
                  }`}
                  onClick={() => {
                    setSelectedKey(option.key);
                    setDropdownOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <span className="eeom-toolbar-title">
          {businessName ? `${businessName} · ` : ''}Expense End-of-Month Report
          {data ? ` · ${cycleLabel}` : ''}
        </span>
      </div>
      <button
        type="button"
        className="eeom-export-btn"
        onClick={handleExport}
        disabled={exporting || !data}
      >
        {exporting ? 'Preparing PDF…' : '⬇ Export PDF'}
      </button>
    </div>
  );

  const shell = (children) => (
    <main className="eeom-root" ref={rootRef}>
      {toolbar}
      {children}
    </main>
  );

  if (loading) return shell(<div style={{ padding: 40 }}>Loading expense analytics…</div>);
  if (error) return shell(<div style={{ color: 'red', padding: 40 }}>Error: {error}</div>);

  if (gated) {
    // Two different refusals, and they need different words. Never having counted is a
    // business-wide state the owner fixes by counting; an uncounted month is a state they
    // fix by counting OR by picking a month that was counted.
    const monthGate = Boolean(selected && !selected.isAll);
    if (gateDismissed) {
      return shell(
        <div style={{ padding: '3rem', textAlign: 'center', color: '#6b6b6b' }}>
          Closing stock isn’t submitted for {selected ? selected.label : 'this period'}. Pick
          another period above, or give closing stock to open this one.
        </div>
      );
    }
    return shell(
      <div className="eeom-gate">
        <div className="eeom-gate-card">
          <button
            type="button"
            className="eeom-gate-close"
            aria-label="Close"
            onClick={() => setGateDismissed(true)}
          >
            ✕
          </button>
          <div className="eeom-gate-icon">🔒</div>
          <h3>Closing stock required</h3>
          <p>
            {monthGate
              ? `${selected.label} has no confirmed closing stock count, so there is nothing
                 reconciled to report for it. Pick a month that was counted, or give closing
                 stock for this one.`
              : `The Expense End-of-Month report can only be generated after you submit
                 closing stock. Once you give closing stock, this report opens automatically
                 and the last count carries forward until you submit a new one.`}
          </p>
          <button
            type="button"
            className="eeom-gate-primary"
            onClick={() => goTo && goTo('closing-stock')}
          >
            Give Closing Stock
          </button>
        </div>
      </div>
    );
  }

  if (!data) return shell(null);

  const ctx = {
    businessName,
    periodText: periodLabel(p.startDate, p.endDate),
    pages: pageList.length,
  };

  const renderPage = (pg, index) => {
    const page = index + 1;
    switch (pg.kind) {
      case 'cover':
        return <Cover key={pg.id} ctx={ctx} />;
      case 'summary':
        return <Summary key={pg.id} data={data} ctx={ctx} page={page} />;
      case 'matrix':
        return <Matrix key={pg.id} data={data} ctx={ctx} page={page} />;
      case 'drivers':
        return <CostDrivers key={pg.id} data={data} ctx={ctx} page={page} />;
      case 'flow':
        return (
          <Page
            key={pg.id}
            ctx={ctx}
            page={page}
            section={pg.section}
            title={pg.title}
            lede={pg.lede}
            continued={pg.continued}
            testid={pg.id}
            flow={pg.flow}
            flowPage={pg.flowPage}
          >
            <FlowBody items={pg.items} />
          </Page>
        );
      case 'decisions':
        return <Decisions key={pg.id} data={data} ctx={ctx} page={page} />;
      default:
        return null;
    }
  };

  return shell(<>{pageList.map(renderPage)}</>);
}
